/**
 * Decision rates — what happens to a deposit, over all traffic.
 * Data: GET /analytics/tt/decision-rates.
 */
import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Tooltip as Hint } from "../../../design/primitives";
import { useTTQuery } from "../ttApi";
import { bucketLabel, count, money, pct } from "../ttFormat";
import { ChartBox, INK, LoadState, Metrics, ReviewCard, ReviewGrid, TONE, axisProps } from "../ttKit";

// `tone` colours the value: green where higher is better, red where it is a loss.
const RATES = [
  { key: "approval", label: "Approval rate", help: "Of the deposits that reached the bank, the share the bank approved", tone: "green" },
  { key: "completed", label: "Completed", help: "Share of all attempts that funded a balance", tone: "green" },
  { key: "block", label: "Block rate", help: "Share of attempts stopped by Idenza before the bank" },
  { key: "issuer_decline_on_allowed", label: "Issuer decline on allowed", help: "Of the attempts we let through, the share the bank refused", tone: "red" },
  { key: "abandonment", label: "Abandonment", help: "Share of attempts the player started but did not finish" },
  { key: "three_ds_attempted", label: "3DS attempted", help: "Of the deposits that reached the bank, the share where 3DS was started" },
  { key: "deposit_yield", label: "Deposit yield", help: "Share of the attempted value that was completed", money: true },
];

const TRENDS = [
  { key: "block", label: "Block rate" },
  { key: "approval", label: "Approval rate" },
  { key: "issuer_decline", label: "Issuer decline on allowed" },
  { key: "abandonment", label: "Abandonment" },
];

export default function DecisionRatesPanel({ win, fresh }) {
  const state = useTTQuery("decision-rates", { window: win, fresh });
  return (
    <LoadState state={state} height={360}>
      {(d) => (
        <>
          <Rates rates={d.rates} scored={d.counts?.scored ?? 0} />
          <Trends trend={d.trend} bucket={d.window?.bucket} />
        </>
      )}
    </LoadState>
  );
}

function Rates({ rates, scored }) {
  const rows = RATES.map((r) => {
    const rate = rates?.[r.key];
    const fmt = r.money ? money : count;
    return {
      name: r.label,
      note: r.help,
      tone: rate ? r.tone : undefined,
      value: rate ? (
        <Hint content={`${fmt(rate.num)} of ${fmt(rate.den)}`}>
          <span>{pct(rate.value)}</span>
        </Hint>
      ) : (
        "—"
      ),
    };
  });
  return (
    <ReviewCard
      eyebrow="Decision rates"
      title="What happens to a deposit"
      desc={`Where every card deposit in this window ended up, across all traffic (${count(scored)} attempts). Hover a rate to see the counts behind it.`}
      tag="All traffic"
    >
      <Metrics rows={rows} />
    </ReviewCard>
  );
}

function Trends({ trend, bucket }) {
  const data = (trend || []).map((t) => ({ label: bucketLabel(t.bucket, bucket), ...t }));
  return (
    <ReviewGrid>
      {TRENDS.map((t) => (
        <ReviewCard key={t.key} eyebrow="Trend" title={t.label} desc={bucket === "hour" ? "Per hour." : "Per day."}>
          <ChartBox height={220}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={INK.line} />
                <XAxis dataKey="label" {...axisProps} minTickGap={16} />
                <YAxis {...axisProps} width={44} tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, "auto"]} />
                <Tooltip formatter={(v) => pct(v)} />
                <Line dataKey={t.key} name={t.label} stroke={TONE.blue.fg} strokeWidth={2} dot={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </ChartBox>
        </ReviewCard>
      ))}
    </ReviewGrid>
  );
}
